import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { categories, collectionsByDate, monthNames, toKey, weekdayNames, type CategoryId } from '../../data/calendrierCollectes';
interface CollectionCalendarProps {
  month: Date;
  selected: Date;
  onMonthChange: (month: Date) => void;
  onSelect: (date: Date) => void;
}
function buildGrid(month: Date): (Date | null)[] {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const first = new Date(year, monthIndex, 1);
  const offset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells: (Date | null)[] = Array.from({
    length: offset
  }, () => null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, monthIndex, day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
export function CollectionCalendar({
  month,
  selected,
  onMonthChange,
  onSelect
}: CollectionCalendarProps) {
  const cells = buildGrid(month);
  const selectedKey = toKey(selected);
  const todayKey = toKey(new Date(2026, 8, 10));
  const legend = Object.values(categories);
  return <section aria-label="Collection calendar" className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-7" data-id="element-611" data-mp="10133wffg5zj9">
      <div className="flex items-center justify-between" data-id="element-612" data-mp="w01c2jnv0prb">
        <button type="button" aria-label="Previous month" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-600 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-613" data-mp="vxeudr798tvd">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-614" data-mp="8aykok3fbijm" />
        </button>
        <h2 aria-live="polite" className="text-base font-bold tracking-[0.14em] text-ville-navy" data-id="element-615" data-mp="3aav82uuf9ri">
          {monthNames[month.getMonth()]} {month.getFullYear()}
        </h2>
        <button type="button" aria-label="Next month" onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-600 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-616" data-mp="9r2mbnyowghe">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-617" data-mp="3mye3sqv1nsjf" />
        </button>
      </div>

      <div className="mt-6 grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-wide text-slate-400" data-id="element-618" data-mp="38a153m4knvl8">
        {weekdayNames.map(day => <span key={day} data-id="element-619" data-mp="2z89y8dyapips">{day}</span>)}
      </div>

      <div className="mt-2 grid grid-cols-7 gap-1" data-id="element-620" data-mp="2za4o86ofc10q">
        {cells.map((date, index) => {
        if (!date) return <span key={`empty-${index}`} aria-hidden="true" data-id="element-621" data-mp="348xrl69zybn6" />;
        const key = toKey(date);
        const events: CategoryId[] = collectionsByDate[key] ?? [];
        const isSelected = key === selectedKey;
        const isToday = key === todayKey;
        return <button key={key} type="button" onClick={() => onSelect(date)} aria-pressed={isSelected} aria-label={`${date.getDate()} ${monthNames[date.getMonth()].toLowerCase()}${events.length ? `, ${events.length} collection(s)` : ', no collection'}`} className={`flex h-14 flex-col items-center justify-center gap-1 rounded-2xl border text-sm transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${isSelected ? 'border-ville-indigo bg-ville-indigo-soft font-bold text-ville-indigo' : 'border-transparent text-slate-700 hover:border-slate-200 hover:bg-slate-50'}`} data-id="element-622" data-mp="2tnc8pt5m9yj2" data-mpi={index}>
              <span className={isToday && !isSelected ? 'font-bold text-ville-navy underline underline-offset-4' : ''} data-id="element-623" data-mp="1fklhiz4fi8a3">
                {date.getDate()}
              </span>
              <span className="flex h-1.5 items-center gap-0.5" data-id="element-624" data-mp="1pd11g1zad6xm">
                {events.slice(0, 4).map(id => <span key={id} className={`h-1.5 w-1.5 rounded-full ${categories[id].dot}`} aria-hidden="true" data-id="element-625" data-mp="1tfnlvhtmnpks" />)}
              </span>
            </button>;
      })}
      </div>

      <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 border-t border-slate-100 pt-5" data-id="element-626" data-mp="11ohr7gwoek2w">
        {legend.map(category => <li key={category.id} className="flex items-center gap-2 text-xs text-slate-500" data-id="element-627" data-mp="1anuutsy4cefp">
            <span className={`h-2 w-2 rounded-full ${category.dot}`} aria-hidden="true" data-id="element-628" data-mp="2h3x2m5bzhn52" />
            {category.label}
          </li>)}
      </ul>
    </section>;
}